import { isReminderEvent } from "@/components/calendars/canEditEvent.ts";
import {
  type EventDetailActions,
  type EventDetailCloseGuard,
  EventDetailPanel,
} from "@/components/calendars/EventDetailPanel.tsx";
import {
  useEventFromCache,
  useEventPanelTimeKey,
} from "@/components/calendars/eventDetailHooks.ts";
import { claimEventProposal } from "@/components/calendars/pendingEventProposal.ts";
import { ReminderDetailPanel } from "@/components/calendars/ReminderDetailPanel.tsx";
import { eventPendingDeleteAtom } from "@/components/calendars/eventDeleteAtom.ts";
import type { CalendarEvent } from "@/types/events.ts";
import { Pencil, Trash2 } from "lucide-react";
import { useSetAtom } from "jotai";
import { useCallback, useRef, useState } from "react";
import { CanvasModalCard } from "./CanvasModalCard.tsx";

/**
 * The canvas card for an event source: the same {@link EventDetailPanel} the
 * event page renders, mounted as one layer of the modal stack. Viewing and
 * editing an event happen here rather than in a right-sidebar.
 *
 * The card's header is the only one: the panel's is suppressed, and the rename
 * and delete it owns are published up and rendered here beside the frame's own
 * controls, so an event doesn't read as two stacked title bars.
 *
 * Every dismissal path the frame owns (Close, Escape, scrim, Back) is routed
 * through the panel's close guard, so unsaved edits prompt before the layer
 * goes away.
 */
export const CanvasEventCard = ({
  event: sourceEvent,
  container,
  showBack,
  isFullscreen,
  onBack,
  onClose,
}: {
  /** The event resolved by the source query — the seed for this card. */
  event: CalendarEvent;
  /** The stack layer this card portals into. */
  container: HTMLElement;
  /** Show the leading Back affordance (this layer sits above the stack root). */
  showBack: boolean;
  /** Whether the presented entity is shown fullscreen. */
  isFullscreen: boolean;
  /** Pop this layer. */
  onBack: () => void;
  /** Clear the whole stack. */
  onClose: () => void;
}) => {
  // Prefer the live calendar cache: socket-driven refetches keep it fresh, while
  // the source query is served from a 60s-stale cache and would hold the copy
  // captured when the card opened. An open card must converge on an external
  // edit (see useEventFromCache), so the source payload only seeds it.
  // An event with no `gcalId` matches nothing in the calendar cache, so the
  // lookup falls through to the source payload — the same "" sentinel the event
  // page uses for a missing route param.
  const cachedEvent = useEventFromCache(sourceEvent.gcalId ?? "");
  const event = cachedEvent ?? sourceEvent;

  // Claimed once, at mount: a drag's "Continue editing" hands its uncommitted
  // times over out of band (see pendingEventProposal) because the canvas
  // addresses this card by URN alone. A later remount re-seeds from the saved
  // event, which is what claiming-clears gives us.
  const [proposal] = useState(() => claimEventProposal(sourceEvent.gcalId ?? ""));

  const [isDirty, setIsDirty] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  // Pristine → remount on an external reschedule; dirty → key frozen so the
  // draft survives (see useEventPanelTimeKey).
  const timeKey = useEventPanelTimeKey(event, isDirty);

  // Clear dirty in the same batch as the remount: waiting for the fresh panel's
  // onDirtyChange(false) would unfreeze timeKey a render later and trigger a
  // second remount when the time changed during the edit.
  const handleCancel = useCallback(() => {
    setIsDirty(false);
    setResetKey((k) => k + 1);
  }, []);

  // Published by EventDetailPanel — routes dismissals through its
  // unsaved-changes prompt. Falls back to proceeding directly if the panel
  // hasn't registered yet, or when a reminder (which has no guard) is shown.
  const closeGuardRef = useRef<EventDetailCloseGuard | null>(null);
  const guardedClose = useCallback((proceed: () => void) => {
    if (closeGuardRef.current) closeGuardRef.current(proceed);
    else proceed();
  }, []);

  const handleClose = useCallback(() => guardedClose(onClose), [guardedClose, onClose]);
  const handleBack = useCallback(() => guardedClose(onBack), [guardedClose, onBack]);

  // Rename and delete live in the card's header rather than a second title bar
  // inside the body. The panel still owns both — they drive its form state and
  // its recurring-scope dialogs — so it publishes triggers and this renders them.
  // State, not a ref: the buttons' presence depends on what it publishes.
  const [panelActions, setPanelActions] = useState<EventDetailActions | null>(null);

  // The panel portals its title here, so renaming edits it in this header rather
  // than opening a second bar below. State, not a ref: the panel can only portal
  // once the node exists, and that has to trigger a render.
  const [titleContainer, setTitleContainer] = useState<HTMLElement | null>(null);

  const panelKey = `${event.gcalId}-${resetKey}-${timeKey}`;

  // Hand the event to the app-level confirmation and close. The dialogs are
  // not rendered here on purpose: this card unmounts on `onClose`, and anything
  // it rendered would go with it (BIG-8266).
  const setEventPendingDelete = useSetAtom(eventPendingDeleteAtom);
  const startDelete = useCallback(() => {
    // Through the guard, and the atom set only once the close is agreed to —
    // see the same handler in EventClickPopover.
    guardedClose(() => {
      setEventPendingDelete(event);
      onClose();
    });
  }, [guardedClose, setEventPendingDelete, event, onClose]);

  return (
    <CanvasModalCard
      // The panel fills this with the live (possibly unsaved) title, and swaps
      // in its rename editor here when that opens.
      title={<span ref={setTitleContainer} className="flex min-w-0 flex-1 items-center" />}
      titleClassName="flex min-w-0 flex-1 items-center gap-2"
      // The title above is a portal target, not text, so the menu needs the words
      // separately. Taken from the panel so an unsaved rename names the menu too,
      // and falling back to the saved name until the panel has reported in.
      titleText={panelActions?.title ?? event.summary}
      // The title's inline rename editor soft-wraps and auto-grows, and it opens
      // in this header. Without the give, a second line renders over the body.
      headerGrows
      container={container}
      showBack={showBack}
      isFullscreen={isFullscreen}
      onBack={handleBack}
      onClose={handleClose}
      headerActions={[
        {
          key: "rename",
          icon: Pencil,
          label: "Rename",
          onClick: () => panelActions?.startRename(),
          statsigName: "event-rename",
          tier: "never",
          visible: !!panelActions?.canRename,
        },
        {
          key: "delete",
          icon: Trash2,
          label: "Delete event",
          onClick: startDelete,
          statsigName: "event-delete",
          tier: "never",
          // Still the panel's answer, not the host's: a reminder publishes
          // no actions at all, and deriving this from the event here would
          // offer Delete on a surface that has no delete.
          visible: !!panelActions?.canDelete,
          iconClassName: "text-destructive",
        },
      ]}
    >
      {/* A reminder gets its own, much smaller panel — almost none of the event
          form applies to one. See ReminderDetailPanel. */}
      {isReminderEvent(event) ? (
        <ReminderDetailPanel key={panelKey} event={event} onClose={onClose} />
      ) : (
        <EventDetailPanel
          key={panelKey}
          event={event}
          prospectiveStart={proposal?.startIso}
          prospectiveEnd={proposal?.endIso}
          onClose={onClose}
          onCancel={handleCancel}
          titleContainer={titleContainer}
          closeGuardRef={closeGuardRef}
          onActionsChange={setPanelActions}
          onDirtyChange={setIsDirty}
          onRequestDelete={startDelete}
          // Same reasoning as the click popover: a card layered on the canvas
          // has nothing to say about the screen a link inside it opens, so it
          // clears rather than sitting in front of it.
          onNavigateAway={onClose}
        />
      )}
    </CanvasModalCard>
  );
};
